/**
 * Event emitter for focusing a node on the canvas without selecting it.
 *
 * Used to navigate to a node on the canvas without opening the details panel.
 * Recent focus requests are replayed for late subscribers so startup deep links
 * are not dropped while the canvas engine is still mounting.
 */

export interface FocusNodeOptions {
  /** Automatic creation/startup focus and Return must not add a navigation entry. */
  rememberOrigin?: boolean
  /** Restore even if the former selected node has been deleted. */
  restoreSceneId?: string | null
  /** Restore the origin camera when returning from a panel reference. */
  viewport?: { x: number; y: number; zoom: number }
  highlight?: boolean
  padding?: number
  maxZoom?: number
  /** Defaults to true. Set false when the destination opens its own editor surface. */
  select?: boolean
}

type FocusNodeListener = (nodeId: string, options?: FocusNodeOptions) => void
type FocusNodesListener = (nodeIds: string[], options?: FocusNodeOptions) => void
type HighlightNodeListener = (nodeId: string | null) => void

const navigationListeners = new Set<FocusNodesListener>()

/** Runs once per new request, before focus subscribers, never during replay. */
export function onBeforeCanvasFocus(listener: FocusNodesListener): () => void {
  navigationListeners.add(listener)
  return () => {
    navigationListeners.delete(listener)
  }
}

const listeners: Set<FocusNodeListener> = new Set()
const multiListeners: Set<FocusNodesListener> = new Set()
const highlightListeners: Set<HighlightNodeListener> = new Set()
const FOCUS_REQUEST_REPLAY_MS = 5000

let lastFocusRequest: { nodeId: string; options?: FocusNodeOptions; emittedAt: number } | null = null
let lastMultiFocusRequest: { nodeIds: string[]; options?: FocusNodeOptions; emittedAt: number } | null = null
let highlightStartTimer: ReturnType<typeof setTimeout> | null = null
let highlightEndTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Subscribe to focus node events.
 * Returns an unsubscribe function.
 */
export function onFocusNode(listener: FocusNodeListener): () => void {
  listeners.add(listener)
  let replayTimer: ReturnType<typeof setTimeout> | null = null
  if (lastFocusRequest && Date.now() - lastFocusRequest.emittedAt <= FOCUS_REQUEST_REPLAY_MS) {
    const { nodeId, options } = lastFocusRequest
    replayTimer = setTimeout(() => {
      if (listeners.has(listener)) {
        try {
          listener(nodeId, options)
        } catch (error) {
          console.error('[FocusNode] Listener error:', error)
        }
      }
    }, 0)
  }
  return () => {
    listeners.delete(listener)
    if (replayTimer) {
      clearTimeout(replayTimer)
    }
  }
}

export function onFocusNodes(listener: FocusNodesListener): () => void {
  multiListeners.add(listener)
  let replayTimer: ReturnType<typeof setTimeout> | null = null
  if (lastMultiFocusRequest && Date.now() - lastMultiFocusRequest.emittedAt <= FOCUS_REQUEST_REPLAY_MS) {
    const { nodeIds, options } = lastMultiFocusRequest
    replayTimer = setTimeout(() => {
      if (!multiListeners.has(listener)) return
      try {
        listener(nodeIds, options)
      } catch (error) {
        console.error('[FocusNodes] Listener error:', error)
      }
    }, 0)
  }
  return () => {
    multiListeners.delete(listener)
    if (replayTimer) clearTimeout(replayTimer)
  }
}

/**
 * Subscribe to highlight node events (temporary visual emphasis).
 * Returns an unsubscribe function.
 */
export function onHighlightNode(listener: HighlightNodeListener): () => void {
  highlightListeners.add(listener)
  return () => {
    highlightListeners.delete(listener)
  }
}

function emitHighlight(nodeId: string | null) {
  highlightListeners.forEach(listener => {
    try {
      listener(nodeId)
    } catch (error) {
      console.error('[HighlightNode] Listener error:', error)
    }
  })
}

/**
 * Emit a focus node event.
 * @param nodeId - The node ID to focus (e.g., "asset-abc123")
 * @param options - Optional highlight and camera settings for the focus action.
 */
export function emitFocusNode(nodeId: string, options: FocusNodeOptions = {}): void {
  navigationListeners.forEach(listener => listener([nodeId], options))
  const { highlight = false } = options
  lastFocusRequest = { nodeId, options, emittedAt: Date.now() }
  listeners.forEach(listener => {
    try {
      listener(nodeId, options)
    } catch (error) {
      console.error('[FocusNode] Listener error:', error)
    }
  })

  if (highlight) {
    if (highlightStartTimer) clearTimeout(highlightStartTimer)
    if (highlightEndTimer) clearTimeout(highlightEndTimer)
    // Delay slightly so the pan animation starts first
    highlightStartTimer = setTimeout(() => emitHighlight(nodeId), 150)
    highlightEndTimer = setTimeout(() => {
      emitHighlight(null)
      highlightStartTimer = null
      highlightEndTimer = null
    }, 1800)
  }
}

export function emitFocusNodes(nodeIds: string[], options: FocusNodeOptions = {}): void {
  if (nodeIds.length === 0) return
  navigationListeners.forEach(listener => listener(nodeIds, options))
  const requestedNodeIds = [...nodeIds]
  lastMultiFocusRequest = { nodeIds: requestedNodeIds, options, emittedAt: Date.now() }
  multiListeners.forEach(listener => {
    try {
      listener(requestedNodeIds, options)
    } catch (error) {
      console.error('[FocusNodes] Listener error:', error)
    }
  })
}
